type Claim = {
  email: string;
  password: string;
  nickname: string;
  claimedAt: string;
};

type Status = {
  remaining: number;
  total: number;
  claimed: number;
  allowMultiple: boolean;
  loginUrl: string;
  dev: boolean;
  claim: Claim | null;
  claims: Claim[];
};

const app = document.getElementById("app")!;
let status: Status | null = null;
let nickname = "";
let loading = true;
let submitting = false;
let error = "";
let copied = "";

async function loadStatus() {
  const res = await fetch("/api/status");
  status = (await res.json()) as Status;
  nickname = status.claim?.nickname ?? nickname;
  loading = false;
  render();
}

async function claim(note = nickname) {
  if (submitting) return;
  submitting = true;
  error = "";
  render();
  try {
    const res = await fetch("/api/claim", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ nickname: note }),
    });
    const data = (await res.json()) as {
      ok: boolean;
      message?: string;
      claim?: Claim;
      remaining?: number;
    };
    if (!data.ok || !data.claim) {
      error = data.message || "领取失败";
    } else {
      await loadStatus();
      return;
    }
  } catch {
    error = "网络有点问题，请再试一次";
  }
  submitting = false;
  render();
}

async function copyText(label: string, value: string) {
  try {
    await navigator.clipboard.writeText(value);
    copied = label;
    render();
    setTimeout(() => {
      if (copied === label) {
        copied = "";
        render();
      }
    }, 1400);
  } catch {
    error = "复制失败，请手动选中文字";
    render();
  }
}

async function resetClaims() {
  if (!status?.dev) return;
  submitting = true;
  error = "";
  render();
  try {
    const res = await fetch("/api/dev/reset-claims", { method: "POST" });
    const data = (await res.json()) as { ok: boolean; message?: string };
    if (!data.ok) {
      error = data.message || "清理失败";
      submitting = false;
      render();
      return;
    }
    nickname = "";
    copied = "";
    submitting = false;
    await loadStatus();
  } catch {
    error = "清理失败，请再试一次";
    submitting = false;
    render();
  }
}

function escapeHtml(value: string) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function hero() {
  return `
    <section>
      <h2 class="text-[1.15rem] font-semibold leading-snug sm:text-[1.25rem]">
        AI 赋能组织与业务—思维重构与落地实操
      </h2>
      <div class="mt-2 grid gap-1.5 text-[12px] text-ink sm:grid-cols-2">
        <p>时间　2026年8月14日（周五）14:00—16:00</p>
        <p>地点　金东区商务中心 B 幢 4 楼</p>
        <p class="text-base font-bold">主办　浙股（金华）企业上市培育服务中心</p>
        <p class="text-base font-bold">演讲　潘扬_Pandison</p>
      </div>
      <p class="kicker mt-4 text-[11px]">法律人专属</p>
      <div class="slogan-pair mt-2">
        <h3 class="slogan-hero slogan-side text-left">
          <span class="slogan-grad">专属小龙虾</span>🦞
        </h3>
        <span class="slogan-and">+</span>
        <h3 class="slogan-hero slogan-side text-right">
          <span class="slogan-grad">专属 Hermes</span>
        </h3>
      </div>
      <p class="slogan-hero mt-3">
        <span class="slogan-grad">使用起来 0 心智负担</span>
      </p>
    </section>
  `;
}

function proofGrid() {
  const proofs = [
    ["零配置", "开箱即用"],
    ["Skill 心法", "首次公开"],
    ["MCP 50+", "法律工具"],
    ["24 小时", "云任务在线"],
    ["持续进化", "越用越聪明"],
  ];

  return `
    <div class="proof-grid mt-3">
      ${proofs
        .map(
          ([title, desc]) => `
            <div class="proof-item rounded-lg px-2.5 py-2">
              <p class="proof-num text-sm">${title}</p>
              <p class="mt-0.5 text-[11px] text-ink-soft">${desc}</p>
            </div>
          `,
        )
        .join("")}
    </div>
  `;
}

function claimCard(claim: Claim, loginUrl: string) {
  const rows = [
    ["账号", claim.email],
    ["密码", claim.password],
  ] as const;

  return `
    <section class="card offer-card mt-4 rounded-xl p-4">
      <div class="flex items-center justify-between">
        <p class="text-[11px] text-onion">账号已就绪</p>
        <p class="text-[11px] text-ink-soft">先复制，再用电脑登录</p>
      </div>
      <div class="mt-3 space-y-2">
        ${rows
          .map(
            ([label, value]) => `
              <div class="flex items-center gap-3 rounded-lg bg-paper px-3 py-2">
                <span class="w-8 shrink-0 text-xs text-ink-soft">${label}</span>
                <p class="min-w-0 flex-1 truncate text-sm font-medium">${escapeHtml(value)}</p>
                <button
                  data-copy-label="${label}"
                  data-copy-value="${escapeHtml(value)}"
                  class="ghost-btn shrink-0 rounded-md px-2 py-1 text-xs text-ink-soft"
                >${copied === label ? "已复制" : "复制"}</button>
              </div>
            `,
          )
          .join("")}
      </div>
      <a
        href="${escapeHtml(loginUrl)}"
        target="_blank"
        rel="noreferrer"
        class="btn-primary mt-3 flex w-full items-center justify-center rounded-lg py-2.5 text-sm text-white"
      >去电脑登录，打开超级任务</a>
      <button data-copy-both class="ghost-btn mt-2 w-full rounded-lg py-2 text-sm">
        ${copied === "账号和密码" ? "账号和密码已复制" : "一键复制账号和密码"}
      </button>
      <p class="mt-2 text-center text-[12px] text-ink-soft">文件拖进页面即可直接上传</p>
      ${proofGrid()}
    </section>
  `;
}

function claimForm(remaining: number) {
  return `
    <section class="card offer-card mt-4 rounded-xl p-4">
      <div class="flex items-end justify-between gap-3">
        <div>
          <p class="text-[11px] text-onion">现在领取</p>
          <h3 class="mt-1 text-base font-medium">领一个账号，马上开用</h3>
        </div>
        <p class="text-xs text-onion">${remaining > 0 ? `还剩 ${remaining} 席` : "已领完"}</p>
      </div>
      <form id="claim-form" class="mt-3 space-y-2">
        <div class="field rounded-lg px-3 py-2.5">
          <input
            id="nickname"
            name="nickname"
            maxlength="40"
            value="${escapeHtml(nickname)}"
            placeholder="名字或昵称，选填"
            class="w-full bg-transparent text-sm text-ink outline-none placeholder:text-hint"
          />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <button
            type="button"
            id="skip-claim"
            class="btn-primary rounded-lg py-2.5 text-sm text-white"
            ${submitting || remaining <= 0 ? "disabled" : ""}
          >${submitting ? "领取中" : remaining <= 0 ? "已领完" : "直接领取"}</button>
          <button
            type="submit"
            class="ghost-btn rounded-lg py-2.5 text-sm"
            ${submitting || remaining <= 0 ? "disabled" : ""}
          >${submitting ? "领取中" : "填写后领取"}</button>
        </div>
      </form>
      ${error ? `<p class="mt-2 text-xs text-onion">${escapeHtml(error)}</p>` : ""}
      ${proofGrid()}
    </section>
  `;
}

function render() {
  const remaining = status?.remaining ?? 0;
  const latest = status?.claim ?? null;
  const locked = Boolean(latest && !status?.allowMultiple);

  app.innerHTML = `
    <div class="mx-auto flex min-h-dvh max-w-[42rem] flex-col px-5 py-4 sm:px-7">
      <header class="flex shrink-0 items-center justify-between">
        <div class="flex items-center gap-2.5">
          <span class="onion-logo" aria-hidden="true"></span>
          <p class="text-sm font-medium">尚书洋葱</p>
        </div>
        <div class="flex items-center gap-2">
          ${
            status?.dev
              ? `<button id="reset-claims" class="ghost-btn rounded-md px-2.5 py-1 text-xs text-ink-soft">清理</button>`
              : ""
          }
          <a href="/admin" class="text-xs text-ink-soft hover:text-onion">管理</a>
        </div>
      </header>

      <main class="flex flex-1 flex-col justify-center py-3">
        ${
          loading
            ? `<div class="card rounded-xl p-4 text-sm text-ink-soft">正在准备领取台…</div>`
            : `
              ${hero()}
              ${
                latest
                  ? claimCard(latest, status?.loginUrl || "https://www.shonion.com/app/")
                  : claimForm(remaining)
              }
              ${
                latest && !locked
                  ? `
                    <form id="claim-form" class="mt-2 flex gap-2">
                      <div class="field min-w-0 flex-1 rounded-lg px-3 py-2">
                        <input
                          id="nickname"
                          name="nickname"
                          maxlength="40"
                          value="${escapeHtml(nickname)}"
                          placeholder="继续领取，选填昵称"
                          class="w-full bg-transparent text-sm text-ink outline-none placeholder:text-hint"
                        />
                      </div>
                      <button
                        type="submit"
                        class="ghost-btn shrink-0 rounded-lg px-4 py-2 text-sm"
                        ${submitting || remaining <= 0 ? "disabled" : ""}
                      >${submitting ? "领取中" : "再领一个"}</button>
                    </form>
                  `
                  : error && locked
                    ? `<p class="mt-2 text-xs text-onion">${escapeHtml(error)}</p>`
                    : ""
              }
            `
        }
      </main>

      <footer class="shrink-0 pt-4 text-center">
        <p class="text-[13px] leading-6 text-ink">
          顶级律师 Skill 心法第一次公开。接入法律相关 MCP 50+，云任务 24 小时不关机，持续进化，越用越聪明。
        </p>
        <p class="mt-2 text-[11px] leading-5 text-ink-soft">
          该 AI 服务由北京尚书洋葱智能科技有限公司提供
          <span class="mx-1">·</span>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer" class="hover:text-onion">京ICP备2026028182号</a>
        </p>
      </footer>
    </div>
  `;

  const form = document.getElementById("claim-form") as HTMLFormElement | null;
  const input = document.getElementById("nickname") as HTMLInputElement | null;
  input?.addEventListener("input", () => {
    nickname = input.value;
  });
  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    void claim(nickname);
  });
  document.getElementById("skip-claim")?.addEventListener("click", () => {
    nickname = "";
    void claim("");
  });

  document.getElementById("reset-claims")?.addEventListener("click", () => {
    void resetClaims();
  });

  for (const button of app.querySelectorAll<HTMLButtonElement>("[data-copy-value]")) {
    button.addEventListener("click", () => {
      void copyText(button.dataset.copyLabel || "内容", button.dataset.copyValue || "");
    });
  }

  const copyBoth = app.querySelector<HTMLButtonElement>("[data-copy-both]");
  copyBoth?.addEventListener("click", () => {
    if (!latest) return;
    void copyText("账号和密码", `账号：${latest.email}\n密码：${latest.password}`);
  });
}

render();
void loadStatus();
